/**
 * Home — R8, restyled to redesign_v3 ("Home mobile" / "Home desktop" frames).
 * Time-based greeting + name + society·flat inside the tinted header band
 * (the band carries the brand lockup since this screen has no TopBar); one
 * urgent-notice hero (the screen's single amber accent moment); "at your
 * gate" visitor card; quick actions 4-grid; recent activity; all-services
 * grid. ≥1024px web renders the 3-column dashboard from the desktop frame.
 * Wired: GET /api/notices (urgent card + recent activity), GET /api/societies
 * (best-effort society name for the subtitle line). Desktop-only additions
 * (maintenance tile, community preview) are real fetches too — GET
 * /api/payments/current (best-effort) and the shared polls/events/issues
 * fetchers — never fabricated numbers.
 *
 * NOTE (deviation, flagged): @expo/vector-icons is not installed in this
 * workspace (Expo SDK 57 no longer bundles it with `expo`, and adding
 * packages is out of scope). Following the precedent already set in
 * features/community/bits.tsx and features/account-admin/icons.tsx, icon
 * moments use the local Feather-style `Icon` set built from Views. One glyph
 * that set doesn't have (a car, for "Vehicles") is added locally below as a
 * small View-based glyph in the same spirit — no shared files touched.
 */
import { LinearGradient } from 'expo-linear-gradient';
import { router, usePathname } from 'expo-router';
import React from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';

import { formatINR, radius, space } from '@bero/tokens';
import { ArchSkyline } from '@/components/brand';
import { BrandBar } from '@/components/shell/BrandBar';
import { str, num } from '@/components/gate/api';
import { fetchCurrentBill, type Bill } from '@/components/money/api';
import { monthYear } from '@/components/money/bits';
import { ArchSectionLabel, UrgentNoticeCard } from '@/features/community/bits';
import {
  fetchEvents, fetchIssues, fetchPolls, timeAgo, useFetch,
  type Issue, type Poll, type SocietyEvent,
} from '@/features/community/lib';
import { Icon, type IconName } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, CountUp, Screen, Skeleton } from '@/ui';

// Once per app session — reopening home shouldn't re-pop a takeover the user closed.
let approvalAutoPopped = false;
// Once per cold start — the full-screen notifications ask (/enable-alerts).
let alertsGateShown = false;

const WIDE = 1024;

/* ---------------- local types + fetch ---------------- */

type NoticeKind = 'urgent' | 'event' | 'committee' | 'general';
type HomeNotice = { id: string; kind: NoticeKind; title: string; when: string };

const KINDS: NoticeKind[] = ['urgent', 'event', 'committee', 'general'];

function toNotice(raw: unknown): HomeNotice | null {
  const id = str(raw, 'id');
  const title = str(raw, 'title');
  if (!id || !title) return null;
  const kindRaw = str(raw, 'kind') ?? 'general';
  const kind = (KINDS as string[]).includes(kindRaw) ? (kindRaw as NoticeKind) : 'general';
  const createdAt = str(raw, 'createdAt');
  const d = createdAt ? new Date(createdAt) : null;
  return { id, kind, title, when: d && !Number.isNaN(d.getTime()) ? timeAgo(d) : '' };
}

async function fetchHomeNotices(): Promise<HomeNotice[]> {
  const body = await api.get<unknown>('/api/notices?per=20');
  const items = Array.isArray((body as { items?: unknown[] })?.items) ? (body as { items: unknown[] }).items : [];
  return items.map(toNotice).filter((n): n is HomeNotice => n !== null);
}

const ACTIVITY_ICON: Record<NoticeKind, IconName> = {
  urgent: 'megaphone', general: 'megaphone', event: 'calendar', committee: 'file-text',
};

/** Desktop-only "Community" column — merges the nearest active poll, the
 * next upcoming event and the top community issue from the shared community
 * fetchers. Real data only; any fetcher that fails is simply omitted. */
type PreviewItem = { id: string; kind: 'Poll' | 'Event' | 'Issue'; title: string; sub: string; time: string; path: string };

async function fetchCommunityPreview(): Promise<PreviewItem[]> {
  const [polls, events, issues] = await Promise.all([
    fetchPolls().catch(() => [] as Poll[]),
    fetchEvents().catch(() => [] as SocietyEvent[]),
    fetchIssues('COMMUNITY').catch(() => [] as Issue[]),
  ]);
  const items: PreviewItem[] = [];
  const poll = polls.find((p) => p.status === 'active');
  if (poll) {
    items.push({ id: `poll-${poll.id}`, kind: 'Poll', title: poll.question, sub: `${poll.votes} of ${poll.total} voted`, time: poll.deadline, path: `/polls/${poll.id}` });
  }
  const event = events.find((e) => !e.past);
  if (event) {
    items.push({ id: `event-${event.id}`, kind: 'Event', title: event.title, sub: event.venue || `${event.going} going`, time: [event.day, event.month].filter(Boolean).join(' '), path: `/events/${event.id}` });
  }
  const issue = issues[0];
  if (issue) {
    items.push({ id: `issue-${issue.id}`, kind: 'Issue', title: issue.title, sub: issue.notify || `${issue.upvotes} upvotes`, time: issue.when, path: `/issues/${issue.id}` });
  }
  return items;
}

/* ---------------- car glyph (Vehicles — not in the shared icon set) ---------------- */
function CarGlyph({ size = 22, color }: { size?: number; color: string }) {
  const s = size / 24;
  const sw = Math.max(1.6, 2 * s);
  return (
    <View style={{ width: size, height: size }}>
      <View style={{
        position: 'absolute', left: 2 * s, top: 9 * s, width: 20 * s, height: 7 * s,
        borderRadius: 3 * s, borderWidth: sw, borderColor: color,
      }} />
      <View style={{
        position: 'absolute', left: 5 * s, top: 5 * s, width: 14 * s, height: 5.5 * s,
        borderTopLeftRadius: 4 * s, borderTopRightRadius: 4 * s,
        borderWidth: sw, borderBottomWidth: 0, borderColor: color,
      }} />
      <View style={{ position: 'absolute', left: 5 * s, top: 14.5 * s, width: 4.5 * s, height: 4.5 * s, borderRadius: 2.25 * s, backgroundColor: color }} />
      <View style={{ position: 'absolute', left: 14.5 * s, top: 14.5 * s, width: 4.5 * s, height: 4.5 * s, borderRadius: 2.25 * s, backgroundColor: color }} />
    </View>
  );
}

/* ---------------- tile (quick actions + all services) ----------------
 * Redesign v3: icon sits inside an arch-shaped chip (large top radii, small
 * bottom radii) instead of a plain square — a light echo of the brand's arch
 * motif on every tile, without introducing a second colored accent. */
function Tile({ icon, custom, label, lines = 1, onPress }: {
  icon?: IconName; custom?: React.ReactNode; label: string; lines?: number; onPress: () => void;
}) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => ({
        // De-boxed (quiet-luxury pass): the arch chip is the tile — no border,
        // no card fill. Pressed state tints the chip area via opacity.
        width: '23%', minHeight: 60, borderRadius: radius.lg,
        opacity: pressed ? 0.6 : 1,
        alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: space.s2, paddingHorizontal: 4,
      })}
    >
      <View style={{
        width: 44, height: 44, backgroundColor: colors.canvas1,
        borderTopLeftRadius: 22, borderTopRightRadius: 22, borderBottomLeftRadius: 7, borderBottomRightRadius: 7,
        alignItems: 'center', justifyContent: 'center',
      }}>
        {custom ?? <Icon name={icon as IconName} size={20} color={colors.textPrimary} />}
      </View>
      <AppText variant="bodySm" weight="500" numberOfLines={lines} style={{ textAlign: 'center', fontSize: 12.5, lineHeight: 16 }}>
        {label}
      </AppText>
    </Pressable>
  );
}


/* ---------------- screen ---------------- */

const QUICK: Array<{ icon: IconName; label: DictKey; path: string }> = [
  { icon: 'user-plus', label: 'qa_visitor', path: '/preapprove' },
  { icon: 'wrench', label: 'qa_helpdesk', path: '/helpdesk' },
  { icon: 'credit-card', label: 'qa_pay', path: '/(tabs)/pay' },
  { icon: 'calendar', label: 'qa_book', path: '/amenities' },
];

type ServiceItem = { icon?: IconName; custom?: boolean; label: DictKey; path: string };

// Grouped app-wide convention (user ask 2026-07-18) — labels beat one big wall.
// Labels are i18n KEYS, resolved with t() at render (Phase A migration).
const SERVICE_GROUPS: { title: DictKey; items: ServiceItem[] }[] = [
  {
    title: 'grp_gate_visitors',
    items: [
      { icon: 'key', label: 'svc_gate_pass', path: '/gatepass' },
      { icon: 'package', label: 'svc_parcels', path: '/parcels' },
      { custom: true, label: 'svc_vehicles', path: '/vehicles' },
      { icon: 'user-plus', label: 'svc_family', path: '/family-pass' },
      { icon: 'user', label: 'svc_kids', path: '/kids-permission' },
      { icon: 'building', label: 'svc_move', path: '/movein-out' },
    ],
  },
  {
    title: 'grp_staff_home',
    items: [
      { icon: 'users', label: 'svc_my_staff', path: '/staff' },
      { icon: 'check', label: 'svc_attendance', path: '/attendance' },
    ],
  },
  {
    title: 'grp_bookings',
    items: [
      { icon: 'pin', label: 'svc_venues', path: '/venue-booking' },
      { icon: 'calendar', label: 'svc_my_bookings', path: '/my-bookings' },
    ],
  },
  {
    title: 'grp_society',
    items: [
      { icon: 'credit-card', label: 'svc_finances', path: '/finances' },
      { icon: 'file-text', label: 'svc_documents', path: '/account/documents' },
      { icon: 'phone', label: 'svc_directory', path: '/account/society-directory' },
      { icon: 'alert-triangle', label: 'svc_sos', path: '/sos' },
    ],
  },
];

function greetingKey(): DictKey {
  const h = new Date().getHours();
  if (h < 12) return 'greeting_morning';
  if (h < 17) return 'greeting_afternoon';
  return 'greeting_evening';
}

function todayLabel(): string {
  return new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Tinted greeting band — a real (subtle) vertical gradient since build #2
 * bakes expo-linear-gradient. Fades from the band tint into the page canvas
 * so the header melts into the content instead of sitting in a box. */
function HeaderBand({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  return (
    <View style={{ position: 'relative', overflow: 'hidden' }}>
      <LinearGradient
        colors={[colors.headerBandTo, colors.headerBandFrom, colors.canvas0]}
        locations={[0, 0.55, 1]}
        style={StyleSheet.absoluteFill}
      />
      {/* faint brand arches — desktop-band decoration only; on narrow screens
          the right:360 anchor leaks a sliver into the left edge. */}
      {width >= 768 ? (
        <View pointerEvents="none" style={{ position: 'absolute', right: 360, bottom: 0, opacity: 0.35 }}>
          <ArchSkyline height={92} width={200} bare />
        </View>
      ) : null}
      {children}
    </View>
  );
}

export default function HomeTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const { user } = useSession();
  const { data: notices, loading, error } = useFetch(fetchHomeNotices, []);
  const [societyName, setSocietyName] = React.useState<string | null>(null);
  // Active-membership flat code (multi-society): reflects the society picked in
  // the switcher, not the legacy user.flat (which is stale after a switch).
  const [activeFlatCode, setActiveFlatCode] = React.useState<string | null>(null);

  // MyGate-style approval takeover (user ask 2026-07-18): if someone is
  // actually waiting at the gate, opening the app lands you ON the approval
  // screen — not on home with a subtle banner. Once per app session; skipped
  // when a notification tap already routed there.
  const pathname = usePathname();
  const pathnameRef = React.useRef(pathname);
  React.useEffect(() => { pathnameRef.current = pathname; }, [pathname]);
  React.useEffect(() => {
    if (approvalAutoPopped || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const body = await api.get<{ items?: unknown[] }>('/api/visitors/pending');
        const waiting = (body.items ?? []).length > 0;
        if (!cancelled && waiting && !approvalAutoPopped && pathnameRef.current === '/home') {
          approvalAutoPopped = true;
          router.push('/visitor-approval');
        }
      } catch {
        // no flat linked / offline — nothing to pop.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const { data: bill, loading: billLoading } = useFetch(() => fetchCurrentBill().catch(() => null as Bill | null), []);
  const { data: preview, loading: previewLoading } = useFetch(fetchCommunityPreview, []);

  // Notifications gate (user rule 2026-07-20): the old dismissible card was
  // too subtle — without alerts the approval loop collapses. Silent
  // re-register when granted; otherwise the FULL-SCREEN /enable-alerts
  // takeover, once per cold start, until granted. The approval takeover
  // wins if a visitor is already waiting (this fires after that check).
  React.useEffect(() => {
    if (alertsGateShown || !user) return;
    let cancelled = false;
    void Promise.resolve().then(async () => {
      const { pushSupported, pushPermissionStatus, registerForPush } = await import('@/lib/push');
      if (!pushSupported()) return;
      const status = await pushPermissionStatus();
      if (cancelled || alertsGateShown) return;
      if (status === 'granted') { void registerForPush(); return; }
      alertsGateShown = true;
      // let a pending approval auto-pop take priority
      setTimeout(() => {
        if (!approvalAutoPopped) router.push('/enable-alerts');
      }, 1200);
    });
    return () => { cancelled = true; };
  }, [user]);

  const PushPrimerCard = null;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The active society + flat come from the membership the X-Bero-Society
        // header resolved to — so the header line follows the switcher.
        const body = await api.get<{ items?: unknown[]; activeSocietyId?: number }>('/api/me/societies');
        const list = Array.isArray(body?.items) ? body.items : [];
        // Account-first / awaiting-approval: a resident with no APPROVED society
        // has nothing to show on home (society-scoped calls 403). Land them on
        // "your flats" to add one / watch a pending request instead. Admins and
        // committee always carry an approved membership, so this never fires for
        // them.
        if (!cancelled && list.length === 0
            && (user?.role === 'RESIDENT' || !user?.role)) {
          router.replace('/account/manage-flats');
          return;
        }
        const active = list.find((s) => num(s, 'societyId') === body?.activeSocietyId) ?? list[0];
        if (cancelled || !active) return;
        const name = str(active, 'societyName');
        const flatCode = str((active as { flat?: unknown })?.flat, 'code');
        if (name) setSocietyName(name);
        setActiveFlatCode(flatCode ?? null);
      } catch {
        // best-effort only — subtitle just falls back to the date line.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.societyId]);

  const urgent = (notices ?? []).find((n) => n.kind === 'urgent') ?? null;
  const recent = (notices ?? []).filter((n) => n.id !== urgent?.id).slice(0, wide ? 6 : 3);
  // Role accounts (Society Office etc.) have no personal first name — greeting
  // them as "Good morning, Society" read like a bug (user report 2026-07-19).
  const isRoleAccount = !user?.flat && (user?.role === 'MASTER_ADMIN' || user?.role === 'SUB_ADMIN');
  const firstName = (user?.fullName ?? '').split(' ')[0] || '';
  const greeting = isRoleAccount || !firstName ? t(greetingKey()) : `${t(greetingKey())}, ${firstName}`;
  const flatCode = activeFlatCode ?? user?.flat?.code ?? null;
  const subtitle = [societyName, flatCode].filter(Boolean).join(' · ');

  {/* Greeting only — the fixed brand strip (search/bell/avatar) lives OUTSIDE
      the scroll now (mobile: BrandBar; this greeting band scrolls beneath it). */}
  const HeaderContent = (
    <View style={{ paddingHorizontal: space.s4, paddingTop: space.s5, paddingBottom: space.s5 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <View style={{ flex: 1 }}>
          <AppText variant="displayLg" display>{greeting}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>
            {wide ? todayLabel() : subtitle || todayLabel()}
          </AppText>
        </View>
        {flatCode ? (
          <View style={{
            borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: space.s3,
            paddingVertical: 7, backgroundColor: colors.canvas0,
          }}>
            <AppText variant="bodySm" weight="600" mono>{flatCode}</AppText>
          </View>
        ) : null}
      </View>
    </View>
  );

  const UrgentSlot = loading ? (
    <Skeleton h={80} />
  ) : urgent ? (
    <UrgentNoticeCard title={urgent.title} when={urgent.when} onPress={() => router.push(`/notices/${urgent.id}`)} />
  ) : error ? (
    <AppText variant="bodySm" tone="tertiary">{t('couldnt_load_notices')}</AppText>
  ) : null;

  const ActivitySlot = (
    <View>
      <ArchSectionLabel>{t('recent_activity')}</ArchSectionLabel>
      {loading ? (
        <View style={{ gap: space.s3 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} h={48} />)}
        </View>
      ) : recent.length === 0 ? (
        <AppText variant="bodySm" tone="tertiary">{t('nothing_new')}</AppText>
      ) : (
        <View>
          {recent.map((n, i) => (
            <View key={n.id}>
              <Pressable
                accessibilityRole="button"
                onPress={() => router.push(`/notices/${n.id}`)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 12 }}
              >
                <View style={{ width: 36, height: 36, borderRadius: radius.full, backgroundColor: colors.canvas1, alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={ACTIVITY_ICON[n.kind]} size={16} color={colors.textSecondary} />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" numberOfLines={1}>{n.title}</AppText>
                  {n.when ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{n.when}</AppText> : null}
                </View>
                <Icon name="chevron-right" size={16} color={colors.textTertiary} />
              </Pressable>
              {i < recent.length - 1 ? <View style={{ height: StyleSheet.hairlineWidth, backgroundColor: colors.borderSoft }} /> : null}
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const QuickActionsSlot = (
    <View>
      <ArchSectionLabel>{t('quick_actions')}</ArchSectionLabel>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', rowGap: space.s2 }}>
        {QUICK.map((q) => (
          <Tile key={q.label} icon={q.icon} label={t(q.label)} onPress={() => router.push(q.path)} />
        ))}
      </View>
    </View>
  );

  const ServicesSlot = (
    <View>
      <ArchSectionLabel>{t('all_services')}</ArchSectionLabel>
      {SERVICE_GROUPS.map((grp, gi) => (
        <View key={grp.title}>
          <AppText variant="label" tone="tertiary"
            style={{ marginTop: gi > 0 ? space.s4 : 0, marginBottom: space.s2 }}>
            {t(grp.title)}
          </AppText>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', rowGap: space.s2, columnGap: space.s2 }}>
            {grp.items.map((s) => (
              <Tile
                key={s.label}
                icon={s.icon}
                custom={s.custom ? <CarGlyph size={20} color={colors.textPrimary} /> : undefined}
                label={t(s.label)}
                lines={2}
                onPress={() => router.push(s.path)}
              />
            ))}
          </View>
        </View>
      ))}
    </View>
  );

  // Desktop: the narrow right column clips 4-across tiles (words wrap and cut
  // — user report 2026-07-17). Rows with full-width labels instead; quick
  // actions fold in on top, then the same groups as mobile.
  const WIDE_GROUPS: { title: DictKey; items: ServiceItem[] }[] = [
    {
      title: 'quick_actions',
      items: [
        { icon: 'user-plus', label: 'qa_preapprove', path: '/preapprove' },
        { icon: 'wrench', label: 'qa_helpdesk', path: '/helpdesk' },
        { icon: 'credit-card', label: 'qa_pay', path: '/(tabs)/pay' },
        { icon: 'calendar', label: 'qa_book_amenity', path: '/amenities' },
      ],
    },
    ...SERVICE_GROUPS,
  ];
  // Single-column rows: a label that ellipsizes ("Pre-app…", "Attenda…")
  // reads as broken — full words, always (user report 2026-07-19).
  const WideServicesSlot = (
    <View>
      <ArchSectionLabel>{t('all_services')}</ArchSectionLabel>
      {WIDE_GROUPS.map((grp, gi) => (
        <View key={grp.title}>
          <AppText variant="label" tone="tertiary"
            style={{ marginTop: gi > 0 ? space.s5 : space.s1, marginBottom: space.s1 }}>
            {t(grp.title)}
          </AppText>
          <View>
            {grp.items.map((s) => (
              <Pressable
                key={s.label}
                accessibilityRole="button"
                accessibilityLabel={s.label}
                onPress={() => router.push(s.path)}
                style={({ pressed }) => ({
                  flexDirection: 'row', alignItems: 'center', gap: space.s3,
                  paddingVertical: 9, paddingHorizontal: 6, borderRadius: radius.md,
                  backgroundColor: pressed ? colors.canvas1 : 'transparent',
                })}
              >
                <View style={{
                  width: 32, height: 32, backgroundColor: colors.canvas1,
                  borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 5, borderBottomRightRadius: 5,
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  {s.custom
                    ? <CarGlyph size={16} color={colors.textPrimary} />
                    : <Icon name={s.icon as IconName} size={15} color={colors.textPrimary} />}
                </View>
                <AppText variant="bodySm" weight="500" style={{ flex: 1 }}>
                  {t(s.label)}
                </AppText>
                <Icon name="chevron-right" size={14} color={colors.textTertiary} />
              </Pressable>
            ))}
          </View>
        </View>
      ))}
    </View>
  );

  if (wide) {
    const dueLabel = bill?.dueDate ? new Date(bill.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
    return (
      <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
        <HeaderBand>
          <View style={{ paddingHorizontal: space.s10, paddingTop: space.s8, paddingBottom: space.s8, flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <AppText variant="displayXl" display>{greeting}</AppText>
              <AppText variant="bodyMd" tone="tertiary" style={{ marginTop: 6 }}>
                {todayLabel()}{subtitle ? `  ·  ${subtitle}` : ''}
              </AppText>
            </View>
            {flatCode ? (
              <View style={{
                borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: space.s3,
                paddingVertical: 8, backgroundColor: colors.canvas0,
              }}>
                <AppText variant="bodySm" weight="600" mono>{flatCode}</AppText>
              </View>
            ) : null}
          </View>
        </HeaderBand>
        {/* De-boxed dashboard (quiet-luxury pass 2026-07-19): columns separated
            by hairlines + air, not nested cards. Only the urgent notice, the
            gate moment and the maintenance tile keep a fill. */}
        <View style={{ flex: 1, flexDirection: 'row', paddingHorizontal: space.s10, paddingTop: space.s6, paddingBottom: space.s6, gap: space.s8 }}>
          <View style={{ flex: 1.2, gap: space.s5 }}>
            {UrgentSlot}
            <View style={{ flex: 1, overflow: 'hidden' }}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {ActivitySlot}
              </ScrollView>
            </View>
          </View>
          <View style={{ width: StyleSheet.hairlineWidth, backgroundColor: colors.borderSoft }} />
          <View style={{ flex: 1, gap: space.s6 }}>
            <View style={{ borderRadius: radius.xl, backgroundColor: colors.canvas1, padding: space.s5 }}>
              <ArchSectionLabel>{t('home_maint_due')}</ArchSectionLabel>
              {billLoading ? (
                <Skeleton w="60%" h={30} style={{ marginTop: space.s2 }} />
              ) : bill ? (
                <>
                  <CountUp value={bill.totalAmount / 100} format={(n) => formatINR(n)} mono weight="500" style={{ fontSize: 30, marginTop: 4 }} />
                  <AppText variant="bodySm" tone="secondary" style={{ marginTop: 3 }}>{monthYear(bill.period)}{dueLabel ? ` · due ${dueLabel}` : ''}</AppText>
                </>
              ) : (
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{t('home_nothing_due')}</AppText>
              )}
              <Pressable
                accessibilityRole="button"
                onPress={() => router.push('/(tabs)/pay')}
                style={({ pressed }) => ({
                  marginTop: space.s4, height: 42, borderRadius: radius.md,
                  backgroundColor: pressed ? colors.primary600 : colors.primary500,
                  alignItems: 'center', justifyContent: 'center',
                })}
              >
                <AppText variant="bodyMd" weight="600" style={{ color: colors.canvas0 }}>{t('home_pay_maint')}</AppText>
              </Pressable>
            </View>
            <View style={{ flex: 1, overflow: 'hidden' }}>
              <ArchSectionLabel>{t('tab_community')}</ArchSectionLabel>
              {previewLoading ? (
                <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={44} />)}</View>
              ) : (preview ?? []).length === 0 ? (
                <AppText variant="bodySm" tone="tertiary">{t('home_community_empty')}</AppText>
              ) : (
                (preview ?? []).map((it, pi) => (
                  <Pressable key={it.id} accessibilityRole="button" onPress={() => router.push(it.path)}
                    style={{ paddingVertical: space.s3, borderTopWidth: pi > 0 ? StyleSheet.hairlineWidth : 0, borderTopColor: colors.borderSoft }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <AppText variant="label" tone="brand">{it.kind.toUpperCase()}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginLeft: 'auto' }}>{it.time}</AppText>
                    </View>
                    <AppText variant="bodyMd" weight="600" style={{ marginTop: 4 }} numberOfLines={1}>{it.title}</AppText>
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }} numberOfLines={1}>{it.sub}</AppText>
                  </Pressable>
                ))
              )}
            </View>
          </View>
          <View style={{ width: StyleSheet.hairlineWidth, backgroundColor: colors.borderSoft }} />
          <View style={{ flex: 0.9 }}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {WideServicesSlot}
            </ScrollView>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      {/* Fixed brand strip — stays put while content scrolls, matching the
          other tab roots. */}
      <BrandBar />
      <Screen padded={false} scroll>
        <HeaderBand>{HeaderContent}</HeaderBand>
        <View style={{ paddingHorizontal: space.s4, paddingTop: space.s4, gap: space.s6 }}>
          {PushPrimerCard}
          {UrgentSlot}
          {QuickActionsSlot}
          {ActivitySlot}
          {ServicesSlot}
        </View>
      </Screen>
    </View>
  );
}
